<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <div class="report-shell sba-slide-in">
      <div class="executive-head">
        <div>
          <label class="active teal-text">Executive Insight</label>
          <h5>Executive Snapshot</h5>
          <p>
            A board-level view of premium received, utilization exposure, MLR,
            high-risk clients, principals, providers, and policy-year movement.
          </p>
        </div>

        <span class="scope-chip">
          {{ selectedCatLabel() }}
          <em>{{ selectedYear || "Current / latest" }}</em>
        </span>
      </div>

      <div class="filter-panel">
        <div class="filter-title">
          <div>
            <label class="active teal-text">Scope</label>
            <h6>Report Filters</h6>
          </div>
          <i class="material-icons">tune</i>
        </div>
        <div class="filter-grid">
          <div>
            <div class="input-field">
              <select v-model="selectedYear" @change="handleSearch">
                <option value="">Current / latest</option>
                <option :value="year" v-for="year in years" :key="year">{{ year }}</option>
              </select>
              <label>Policy Start Year</label>
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="selectedCat" @change="handleSearch">
                <option :value="cat.value" v-for="cat in clientCats" :key="cat.value">{{ cat.label }}</option>
              </select>
              <label>Category</label>
            </div>
          </div>
          <div class="report-actions">
            <button class="btn-small waves-effect waves-light grey action-button" @click.prevent="handleClear">
              <i class="material-icons left">clear</i>Clear
            </button>
            <button class="btn-small waves-effect waves-light cyan darken-3 action-button" @click.prevent="handleSearch">
              <i class="material-icons left">search</i>Search
            </button>
            <button class="btn-small waves-effect waves-light cyan darken-3 action-button" @click.prevent="downloadOverview">
              <i class="material-icons left">download</i>Download
            </button>
          </div>
        </div>
      </div>

      <div class="kpi-grid">
        <div class="summary-panel clients">
          <small>Clients</small>
          <h6>{{ report.summary.clients_count }}</h6>
        </div>
        <div class="summary-panel premium">
          <small>Premium Received</small>
          <h6>{{ formatMoney(report.summary.invoice_paid) }}</h6>
        </div>
        <div class="summary-panel utilization">
          <small>Gross Utilization</small>
          <h6>{{ formatMoney(report.summary.gross_utilization ?? report.summary.utilization_sent) }}</h6>
          <em>{{ formatMoney(report.summary.real_utilization) }} real</em>
        </div>
        <div class="summary-panel">
          <small>Gross MLR</small>
          <h6 :class="mlrClass(report.summary.gross_mlr ?? report.summary.utilization_rate)">{{ report.summary.gross_mlr ?? report.summary.utilization_rate }}%</h6>
          <em>{{ report.summary.real_mlr ?? 0 }}% real</em>
        </div>
      </div>

      <div class="secondary-panel">
        <div class="metric-grid">
          <div class="metric-cell">
            <span class="metric-icon"><i class="material-icons">receipt_long</i></span>
            <div>
              <small>Total Invoices</small>
              <strong>{{ formatMoney(report.summary.invoice_generated) }}</strong>
            </div>
          </div>
          <div class="metric-cell">
            <span class="metric-icon"><i class="material-icons">pending_actions</i></span>
            <div>
              <small>Pending Invoices</small>
              <strong>{{ formatMoney(report.summary.invoice_pending) }}</strong>
            </div>
          </div>
          <div class="metric-cell">
            <span class="metric-icon"><i class="material-icons">medical_services</i></span>
            <div>
              <small>Encounters</small>
              <strong>{{ report.summary.encounters_count }}</strong>
            </div>
          </div>
          <div class="metric-cell">
            <span class="metric-icon"><i class="material-icons">priority_high</i></span>
            <div>
              <small>Over-utilized Clients</small>
              <strong>{{ report.over_utilized_clients.length }}</strong>
            </div>
          </div>
        </div>

        <div class="band-strip">
          <div class="band-item" v-for="band in report.utilization_bands" :key="band.key">
            <span class="band-marker"></span>
            <div>
              <strong>{{ band.label }}</strong>
              <small>{{ band.clients_count }} clients</small>
            </div>
            <span>{{ band.gross_mlr ?? band.utilization_rate }}% Gross MLR</span>
          </div>
        </div>
      </div>

      <div class="insight-grid trend-grid">
        <div class="report-section">
          <div class="section-header">
            <div>
              <h6>Monthly Utilization</h6>
              <small>Bar compares each month against the highest month in this scope.</small>
            </div>
          </div>
          <div class="measure-row" v-for="month in report.monthly_utilization" :key="month.month">
            <div class="measure-label">
              <strong>{{ month.month }}</strong>
              <small>{{ month.encounter_count }} encounters</small>
            </div>
            <div class="measure-track" :title="`${monthlyWidth(month.sent)}% of highest month`">
              <span :style="`width: ${monthlyWidth(month.sent)}%`"></span>
            </div>
            <div class="measure-value">
              <strong>{{ formatMoney(month.gross_sent ?? month.sent) }}</strong>
              <small>{{ formatMoney(month.real_approved) }} real</small>
            </div>
          </div>
          <p class="center grey-text" v-if="!report.monthly_utilization.length">
            No utilization trend found.
          </p>
        </div>

        <div class="report-section">
          <div class="section-header">
            <div>
              <h6>Policy Years</h6>
              <small>Bar shows MLR, capped visually at 100%.</small>
            </div>
          </div>
          <div class="measure-row policy-row" v-for="year in report.policy_years" :key="year.year">
            <div class="measure-label">
              <strong>{{ year.year }}</strong>
              <small>{{ year.clients_count }} clients</small>
            </div>
            <div class="measure-track" :title="`${year.gross_mlr ?? year.utilization_rate}% Gross MLR`">
              <span :class="barClass(year.gross_mlr ?? year.utilization_rate)" :style="`width: ${barWidth(year.gross_mlr ?? year.utilization_rate)}%`"></span>
            </div>
            <div class="measure-value">
              <strong>{{ year.gross_mlr ?? year.utilization_rate }}% Gross MLR</strong>
              <small>{{ year.real_mlr ?? 0 }}% real</small>
            </div>
          </div>
          <p class="center grey-text" v-if="!report.policy_years.length">
            No policy year summary found.
          </p>
        </div>
      </div>

      <div class="insight-grid">
        <div class="report-section">
          <div class="section-header">
            <h6>Top Clients</h6>
          </div>
          <div class="rank-row" v-for="client in report.top_clients" :key="client.id">
            <i class="material-icons" :class="{ danger: client.utilization_rate >= 100 }">
              {{ client.utilization_rate >= 100 ? "flag" : "business" }}
            </i>
            <div>
              <strong>{{ client.name }}</strong>
              <small>{{ client.cat?.toUpperCase() || "" }} | {{ client.policy?.year || "" }} | {{ policyDateRange(client.policy) }}</small>
            </div>
            <div class="rank-value">
              <strong>{{ client.gross_mlr ?? client.utilization_rate }}% Gross MLR</strong>
              <small>{{ client.real_mlr ?? 0 }}% real | {{ formatMoney(client.encounter.real_approved) }}</small>
            </div>
          </div>
          <p class="center grey-text" v-if="!report.top_clients.length">
            No client utilization found.
          </p>
        </div>

        <div class="report-section">
          <div class="section-header">
            <h6>High-Risk Principals</h6>
          </div>
          <div class="rank-row" v-for="principal in report.high_risk_principals" :key="`${principal.id}-${principal.policy?.id}`">
            <i class="material-icons" :class="{ danger: principal.utilization_rate >= 100 }">person</i>
            <div>
              <strong>{{ principal.name }}</strong>
              <small>{{ principal.type === "f" ? "Family" : "Individual" }} | {{ principal.policy?.year || "" }} | {{ policyDateRange(principal.policy) }}</small>
            </div>
            <div class="rank-value">
              <strong>{{ principal.gross_mlr ?? principal.utilization_rate }}% Gross MLR</strong>
              <small>{{ principal.real_mlr ?? 0 }}% real | {{ formatMoney(principal.encounter.real_approved) }}</small>
            </div>
          </div>
          <p class="center grey-text" v-if="!report.high_risk_principals.length">
            No high-risk principals found.
          </p>
        </div>
      </div>

      <div class="insight-grid provider-grid">
        <div class="report-section">
          <div class="section-header">
            <h6>Top Providers</h6>
          </div>
          <div class="table-row" v-for="provider in report.top_providers" :key="provider.provider_id">
            <strong>{{ provider.provider?.name || "Provider" }}</strong>
            <span>{{ provider.encounter_count }} encounters</span>
            <div class="right-align">
              <strong>{{ formatMoney(provider.gross_sent ?? provider.sent) }}</strong><br />
              <small>{{ formatMoney(provider.real_approved) }} real</small>
            </div>
          </div>
          <p class="center grey-text" v-if="!report.top_providers.length">
            No provider utilization found.
          </p>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import { computed, onMounted, onUpdated, ref } from "vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import M from "materialize-css";

export default {
  name: "reportInsightComponent",
  components: { LoadingDiv },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const selectedYear = ref("");
    const selectedCat = ref("hmo");

    const clientCats = ref([
      { value: "hmo", label: "Private" },
      { value: "retail", label: "Retail" },
      { value: "tpa", label: "TPA" },
      // { value: "schemes", label: "Schemes" },
    ]);

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const years = computed(() => {
      const year = new Date().getFullYear();
      return [year, year - 1, year - 2, year - 3, year - 4];
    });

    const buildUrl = () => {
      const params = [];

      if (selectedYear.value) {
        params.push(`year=${selectedYear.value}`);
      }

      if (selectedCat.value) {
        params.push(`cat=${selectedCat.value}`);
      }

      return params.length
        ? `operator/insight/utilization/overview?${params.join("&")}`
        : "operator/insight/utilization/overview";
    };

    const getOverview = () => {
      (async () => {
        const { res, err } = await getItems(buildUrl());

        if (res.value) {
          response.value = res.value.data;
          error.value = null;
        } else {
          response.value = emptyReport;
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(getOverview);

    const handleSearch = () => {
      isReady.value = false;
      getOverview();
    };

    const handleClear = () => {
      selectedYear.value = "";
      selectedCat.value = "hmo";
      handleSearch();
    };

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const barWidth = (val) => {
      return Math.min(Number(val || 0), 100);
    };

    const barClass = (val) => {
      if (val >= 100) return "red darken-1";
      if (val >= 75) return "orange darken-1";
      if (val >= 50) return "purple darken-1";
      if (val > 0) return "green darken-1";
      return "blue darken-1";
    };

    const mlrClass = (val) => {
      const rate = Number(val || 0);

      if (rate >= 100) return "mlr-red";
      if (rate >= 71) return "mlr-orange";
      return "mlr-green";
    };

    const monthlyWidth = (sent) => {
      const max = Math.max(...report.value.monthly_utilization.map((x) => Number(x.sent || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(sent || 0) / max) * 100);
    };

    const formatDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const policyDateRange = (policy) => {
      if (!policy?.start_date || !policy?.end_date) return "";
      return `${formatDate(policy.start_date)} - ${formatDate(policy.end_date)}`;
    };

    const selectedCatLabel = () => {
      return clientCats.value.find((cat) => cat.value === selectedCat.value)?.label || "Private";
    };

    const downloadOverview = () => {
      const data = [];

      data.push({
        No: "",
        Section: "Summary",
        Name: "Overall",
        Clients: report.value.summary.clients_count,
        "Total Invoices": report.value.summary.invoice_generated,
        "Premium Received": report.value.summary.invoice_paid,
        "Pending Invoices": report.value.summary.invoice_pending,
        Encounters: report.value.summary.encounters_count,
        "Gross Utilization": report.value.summary.gross_utilization ?? report.value.summary.utilization_sent,
        "Real Utilization": report.value.summary.real_utilization,
        "Gross MLR": `${report.value.summary.gross_mlr ?? report.value.summary.utilization_rate}%`,
        "Real MLR": `${report.value.summary.real_mlr ?? 0}%`,
      });

      data.push({});

      report.value.utilization_bands.forEach((band, index) => {
        data.push({
          No: index + 1,
          Section: "Utilization bands",
          Name: band.label,
          Clients: band.clients_count,
          "Total Invoices": band.invoice_generated,
          "Premium Received": band.invoice_paid,
          "Pending Invoices": band.invoice_pending,
          "Gross Utilization": band.gross_utilization ?? band.utilization_sent,
          "Real Utilization": band.real_utilization,
          "Gross MLR": `${band.gross_mlr ?? band.utilization_rate}%`,
          "Real MLR": `${band.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      report.value.top_clients.forEach((client, index) => {
        data.push({
          No: index + 1,
          Section: "Top clients",
          Name: client.name,
          Category: client.cat?.toUpperCase() || "",
          Policy: client.policy?.year || "",
          "Policy Start Date": client.policy?.start_date || "",
          "Policy End Date": client.policy?.end_date || "",
          "Total Invoices": client.invoice.generated,
          "Premium Received": client.invoice.paid,
          "Pending Invoices": client.invoice.pending,
          Encounters: client.encounter.count,
          "Gross Utilization": client.encounter.gross_sent ?? client.encounter.sent,
          "Real Utilization": client.encounter.real_approved,
          "Gross MLR": `${client.gross_mlr ?? client.utilization_rate}%`,
          "Real MLR": `${client.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      report.value.over_utilized_clients.forEach((client, index) => {
        data.push({
          No: index + 1,
          Section: "Over-utilized clients",
          Name: client.name,
          Category: client.cat?.toUpperCase() || "",
          Policy: client.policy?.year || "",
          "Policy Start Date": client.policy?.start_date || "",
          "Policy End Date": client.policy?.end_date || "",
          "Total Invoices": client.invoice.generated,
          "Premium Received": client.invoice.paid,
          "Pending Invoices": client.invoice.pending,
          Encounters: client.encounter.count,
          "Gross Utilization": client.encounter.gross_sent ?? client.encounter.sent,
          "Real Utilization": client.encounter.real_approved,
          "Gross MLR": `${client.gross_mlr ?? client.utilization_rate}%`,
          "Real MLR": `${client.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      report.value.high_risk_principals.forEach((principal, index) => {
        data.push({
          No: index + 1,
          Section: "High-risk principals",
          Name: principal.name,
          Type: principal.type === "f" ? "Family" : "Individual",
          Policy: principal.policy?.year || "",
          "Policy Start Date": principal.policy?.start_date || "",
          "Policy End Date": principal.policy?.end_date || "",
          "Total Invoices": principal.invoice.generated,
          "Premium Received": principal.invoice.paid,
          "Pending Invoices": principal.invoice.pending,
          Encounters: principal.encounter.count,
          "Gross Utilization": principal.encounter.gross_sent ?? principal.encounter.sent,
          "Real Utilization": principal.encounter.real_approved,
          "Gross MLR": `${principal.gross_mlr ?? principal.utilization_rate}%`,
          "Real MLR": `${principal.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      report.value.monthly_utilization.forEach((month, index) => {
        data.push({
          No: index + 1,
          Section: "Monthly utilization",
          Month: month.month,
          Encounters: month.encounter_count,
          "Gross Utilization": month.gross_sent ?? month.sent,
          "Real Utilization": month.real_approved,
        });
      });

      data.push({});

      report.value.top_providers.forEach((provider, index) => {
        data.push({
          No: index + 1,
          Section: "Top providers",
          Name: provider.provider?.name || "Provider",
          Encounters: provider.encounter_count,
          "Gross Utilization": provider.gross_sent ?? provider.sent,
          "Real Utilization": provider.real_approved,
        });
      });

      data.push({});

      report.value.policy_years.forEach((year, index) => {
        data.push({
          No: index + 1,
          Section: "Policy years",
          Year: year.year,
          Clients: year.clients_count,
          "Total Invoices": year.invoice_generated,
          "Premium Received": year.invoice_paid,
          "Pending Invoices": year.invoice_pending,
          Encounters: year.encounters_count,
          "Gross Utilization": year.gross_utilization ?? year.utilization_sent,
          "Real Utilization": year.real_utilization,
          "Gross MLR": `${year.gross_mlr ?? year.utilization_rate}%`,
          "Real MLR": `${year.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      if (data.length) {
        downloadExcelFx(data, `Executive_Utilization_${selectedYear.value || "Current_Latest"}_${selectedCatLabel()}`);
      }
    };

    const emptyReport = {
      summary: {
        clients_count: 0,
        invoice_generated: 0,
        invoice_paid: 0,
        invoice_pending: 0,
        encounters_count: 0,
        utilization_sent: 0,
        gross_utilization: 0,
        real_utilization: 0,
        utilization_rate: 0,
        gross_mlr: 0,
        real_mlr: 0,
      },
      top_clients: [],
      over_utilized_clients: [],
      utilization_bands: [],
      monthly_utilization: [],
      top_providers: [],
      high_risk_principals: [],
      policy_years: [],
      scheme_trends: [],
    };

    const report = computed(() => response.value || emptyReport);

    return {
      handleSearch,
      handleClear,
      downloadOverview,
      formatMoney,
      selectedCatLabel,
      policyDateRange,
      barWidth,
      barClass,
      mlrClass,
      monthlyWidth,
      isReady,
      error,
      selectedYear,
      selectedCat,
      clientCats,
      years,
      report,
    };
  },
};
</script>

<style scoped>
.m-0 {
  margin: 0;
}

.report-shell {
  color: #263238;
  display: grid;
  gap: 16px;
  margin-top: 8px;
}

.executive-head,
.filter-panel,
.summary-panel,
.secondary-panel,
.report-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: none;
}

.executive-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 142px;
  padding: 28px;
}

.executive-head h5 {
  color: #263238;
  font-size: 1.24rem;
  font-weight: 650;
  margin: 4px 0 0;
}

.executive-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 780px;
}

.scope-chip {
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  box-shadow: none;
  color: #455a64;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.3;
  padding: 6px 10px;
  text-align: right;
}

.scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.kpi-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.summary-panel {
  border: 0;
  color: #ffffff;
  min-height: 124px;
  padding: 24px;
  position: relative;
}

.summary-panel::before {
  border-radius: 4px;
  bottom: 14px;
  content: "";
  left: 7px;
  position: absolute;
  top: 14px;
  width: 4px;
}

.summary-panel.clients {
  background: #1565c0;
}

.summary-panel.clients::before {
  background: rgba(255, 255, 255, 0.55);
}

.summary-panel.clients h6 {
  color: #ffffff;
}

.summary-panel.premium {
  background: #00897b;
}

.summary-panel.premium::before {
  background: rgba(255, 255, 255, 0.55);
}

.summary-panel.utilization {
  background: #ef6c00;
}

.summary-panel.utilization::before {
  background: rgba(255, 255, 255, 0.55);
}

.summary-panel.utilization h6 {
  color: #ffffff;
}

.summary-panel:not(.clients):not(.premium):not(.utilization) {
  background: #c62828;
}

.summary-panel:not(.clients):not(.premium):not(.utilization)::before {
  background: rgba(255, 255, 255, 0.55);
}

.summary-panel h6 {
  color: #ffffff;
  font-size: 1.42rem;
  font-weight: 700;
  margin: 10px 0 0;
  overflow-wrap: anywhere;
}

.summary-panel h6.mlr-green {
  color: #ffffff;
}

.summary-panel h6.mlr-orange {
  color: #ffffff;
}

.summary-panel h6.mlr-red {
  color: #ffffff;
}

.metric-cell small,
.band-item small,
.section-header small,
.measure-label small,
.rank-row small,
.table-row span {
  color: #607d8b;
}

.summary-panel small {
  color: rgba(255, 255, 255, 0.86);
}

.summary-panel em {
  color: rgba(255, 255, 255, 0.82);
  display: block;
  font-size: 0.8rem;
  font-style: normal;
  margin-top: 5px;
}

.summary-panel.premium h6 {
  color: #ffffff;
}

.summary-panel.utilization h6 {
  color: #ffffff;
}

.filter-panel {
  background: #f7fafb;
  margin: 0;
  padding: 24px;
}

.filter-title {
  align-items: flex-start;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 16px;
}

.filter-title h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.filter-title i {
  color: #90a4ae;
  font-size: 24px;
}

.filter-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) minmax(280px, auto);
  align-items: end;
}

.filter-grid .input-field {
  margin: 0;
}

.report-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.action-button {
  display: flex;
  justify-content: center;
  margin: 0;
  white-space: nowrap;
}

.secondary-panel,
.report-section {
  padding: 24px;
}

.metric-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-cell {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  display: flex;
  gap: 13px;
  min-height: 76px;
  padding: 16px;
  position: relative;
}

.metric-cell:nth-child(1) .metric-icon {
  background: #1565c0;
}

.metric-cell:nth-child(2) .metric-icon {
  background: #f57c00;
}

.metric-cell:nth-child(3) .metric-icon {
  background: #00897b;
}

.metric-cell:nth-child(4) .metric-icon {
  background: #c62828;
}

.metric-icon {
  align-items: center;
  border-radius: 999px;
  color: #ffffff;
  display: flex;
  flex-shrink: 0;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.metric-icon i {
  font-size: 20px;
}

.metric-cell small,
.metric-cell strong {
  display: block;
}

.metric-cell strong {
  color: #263238;
  font-size: 1.05rem;
  margin-top: 5px;
}

.band-strip {
  border-top: 1px solid #e7e7e7;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 18px;
  padding-top: 16px;
}

.band-item {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  min-height: 70px;
  padding: 15px 14px;
}

.band-item div {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.band-marker {
  border-radius: 999px;
  flex-shrink: 0;
  margin-top: 4px;
  height: 11px;
  width: 11px;
}

.band-item:nth-child(1) {
  background: #e8f5e9;
  border-color: #c8e6c9;
}

.band-item:nth-child(1) .band-marker {
  background: #2e7d32;
}

.band-item:nth-child(2) {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.band-item:nth-child(2) .band-marker {
  background: #1565c0;
}

.band-item:nth-child(3) {
  background: #fff3e0;
  border-color: #ffe0b2;
}

.band-item:nth-child(3) .band-marker {
  background: #ef6c00;
}

.band-item:nth-child(4) {
  background: #ffebee;
  border-color: #ffcdd2;
}

.band-item:nth-child(4) .band-marker {
  background: #c62828;
}

.band-item span {
  font-weight: 600;
}

.section-header {
  align-items: flex-start;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 14px;
}

.section-header h6 {
  color: #263238;
  font-weight: 700;
  margin: 0;
}

.insight-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.trend-grid {
  grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);
}

.provider-grid {
  grid-template-columns: minmax(0, 1fr);
}

.measure-row {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 120px 1fr 150px;
  padding: 13px 0;
}

.measure-label strong,
.measure-label small {
  display: block;
}

.measure-track {
  background: #edf2f4;
  border-radius: 999px;
  height: 9px;
  overflow: hidden;
}

.measure-track span {
  background: #00838f;
  display: block;
  height: 9px;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.measure-track:hover span {
  filter: saturate(1.15);
}

.measure-value {
  text-align: right;
}

.measure-value strong,
.measure-value small {
  display: block;
}

.measure-value small {
  color: #78909c;
  font-size: 0.76rem;
}

.rank-row {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 12px;
  grid-template-columns: 34px 1fr auto;
  padding: 14px 0;
}

.rank-row i {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.rank-row i.danger {
  background: #ffebee;
  color: #d32f2f;
}

.rank-row strong,
.rank-row small,
.rank-value strong,
.rank-value small {
  display: block;
}

.rank-value {
  text-align: right;
  white-space: nowrap;
}

.table-row {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto auto;
  padding: 14px 0;
}

.table-row strong:last-child {
  text-align: right;
}

.report-section > :last-child,
.rank-row:last-of-type,
.table-row:last-of-type {
  border-bottom: 0;
}

@media only screen and (max-width: 992px) {
  .kpi-grid,
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid,
  .insight-grid,
  .trend-grid {
    grid-template-columns: 1fr;
  }

  .band-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .measure-row {
    grid-template-columns: 110px 1fr;
  }

  .measure-value {
    grid-column: 2;
  }

}

@media only screen and (max-width: 600px) {
  .executive-head {
    flex-direction: column;
  }

  .scope-chip {
    text-align: left;
  }

  .kpi-grid,
  .metric-grid,
  .band-strip {
    grid-template-columns: 1fr;
  }

  .measure-row,
  .rank-row,
  .table-row {
    grid-template-columns: 1fr;
  }

  .report-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }

  .rank-row i {
    display: none;
  }

  .rank-value,
  .measure-value,
  .table-row strong:last-child {
    text-align: left;
  }
}
</style>
